<script setup lang="ts">
import ProgressStateIcon from '@panel/components/app/ProgressStateIcon.vue';
import { useElementSize, useScroll } from '@vueuse/core';
import { ref, watch, watchEffect } from 'vue';

const props = withDefaults(
  defineProps<{
    title?: string;
    sectionsMapping: Record<
      string,
      {
        label: string;
        isVisible: boolean;
        isMounted: boolean;
        isLoading?: boolean;
        isDone?: boolean;
        isInvalid?: boolean;
      }
    >;
  }>(),
  { title: 'Summary' },
);

const firstVisibleSection = ref<string>('');
const { arrivedState } = useScroll(window);
const { height } = useElementSize(document.body);

watch(
  () => height.value,
  () => {
    arrivedState.bottom = false;
  },
);

const isDoneMapping = ref(Object.values(props.sectionsMapping).map(({ isDone }) => isDone));
const isInvalidMapping = ref(
  Object.values(props.sectionsMapping).map(({ isInvalid }) => isInvalid),
);

const delayStart = ref(-1);

watchEffect((onInvalidate) => {
  const entries = Object.entries(props.sectionsMapping);
  const activeSections = entries.filter(([_, properties]) => properties.isMounted);

  // always highlight the last item, if we arrived at the bottom
  if (arrivedState.bottom) {
    firstVisibleSection.value = activeSections.at(activeSections.length - 1)[0];
    return;
  }

  const visibleSection = activeSections.find(([_, properties]) => properties.isVisible);
  if (visibleSection != null) {
    firstVisibleSection.value = visibleSection[0];
  }

  // sync isDone states
  const timeoutId = setTimeout(() => {
    delayStart.value = -1;

    // here we want to collect all isDone states for all mounted sections, to avoid isDone state changes without respect of the order of the sections
    for (let index = 0; index < entries.length; index += 1) {
      const [, { isDone, isMounted, isInvalid }] = entries[index];
      const currentIsDone = isDoneMapping.value[index];

      if (index > 0 && isMounted) {
        const previousEntry = entries[index - 1];

        // we have to sync isInvalid with isDone state in the timeout
        isInvalidMapping.value[index] = isInvalid;

        // isDone should be applied to the current section, only, if the previous section already has any flag set to true
        if (
          isDone &&
          (previousEntry[1].isDone || previousEntry[1].isInvalid || previousEntry[1].isLoading)
        ) {
          isDoneMapping.value[index] = true;
        }
        // force set isDone to false
        else if (!isDone) {
          isDoneMapping.value[index] = false;
        }
      } else if (index === 0) {
        isDoneMapping.value[index] = isDone;
      }

      // if the isDone state changes in the current run, we have to set a delay transition start
      // for instance the first transtion starts at third section, assign delayStart to 2
      // the transition delay would be 0ms for the third section (index 2), hence 2 - 2 -> 0ms delay, 3 - 2 -> 100ms delay, 4 - 2 -> 200ms delay and so on
      if (currentIsDone !== isDoneMapping.value[index] && delayStart.value === -1) {
        delayStart.value = index;
      }
    }
  }, 500);

  onInvalidate(() => {
    if (timeoutId != null) {
      // the timeout callback should only run, if no dependency in the effect does not changing anymore within 500ms
      clearTimeout(timeoutId);
    }
  });
});

const scrollToSection = (id: string) => {
  const section = document.getElementById(id);
  if (section == null) return;
  document.documentElement.scrollTo({ top: section.offsetTop - 50, behavior: 'smooth' });
};
</script>

<template>
  <div
    class="order:tw-sticky order:tw-top-20 order:tw-ml-0 order:tw-w-80 order:tw-min-h-full tw-fixed tw-bottom-0 tw-w-full tw-rounded tw-bg-gp-midnight-1 tw-p-4 tw-shadow md:tw-px-8 xl:tw-left-80 xl:tw-right-0 xl:tw-w-auto"
  >
    <slot name="header">
      <p class="tw-text-2xl tw-font-bold">{{ title }}</p>
    </slot>
    <ul class="order:tw-block tw-mt-2 tw-flex tw-gap-4 tw-overflow-auto">
      <li
        v-for="(section, id, index) in sectionsMapping"
        :key="id"
        class="order:tw-mb-2 order:tw-text-left"
      >
        <a
          :href="`#${id}`"
          @click.prevent="scrollToSection(id)"
          class="tw-relative tw-flex tw-items-center tw-whitespace-nowrap tw-rounded tw-px-2 tw-py-1 tw-text-center tw-text-gp-mouse tw-transition-colors hover:tw-bg-gp-midnight-2"
          :class="{
            'tw-bg-transparent hover:tw-bg-gp-midnight-2':
              firstVisibleSection !== id && section.isMounted,
            'tw-cursor-default tw-bg-gp-midnight-2': firstVisibleSection === id,
            'tw-pointer-events-none tw-opacity-70': !section.isMounted,
          }"
        >
          <ProgressStateIcon
            class="tw-mr-4"
            :is-done="isDoneMapping[index]"
            :is-loading="section.isLoading"
            :is-invalid="isInvalidMapping[index]"
            :delay="(index - delayStart) * 100"
          />
          {{ section.label }}
        </a>
      </li>
    </ul>

    <slot name="footer"></slot>
  </div>
</template>
